import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { Pressable, StyleSheet, View } from "react-native";
import { useGetAiTutorQuery } from "../graphql/generated/graphql";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import LTILayout from "../layouts/LTILayout";
import type { RootStackParamList } from "../navigation/navigation.types";
import { Feather } from "@expo/vector-icons";

function LTIAnalyticsView() {
  const navigation = useNavigation();
  const route = useRoute<RouteProp<RootStackParamList, "LTI Analytics">>();
  const { activityId } = route.params;

  const { data, loading } = useGetAiTutorQuery({
    variables: { id: activityId },
    skip: !activityId,
  });

  const fetchedTitle = data?.getAITutor?.data?.[0]?.title;
  const title = fetchedTitle || "Untitled Activity";

  return (
    <LTILayout>
      {loading ? (
        <View style={styles.loadingContainer}>
          <CustomText text="Loading..." size="m" useTranslationText={false} style={styles.loadingText} />
        </View>
      ) : (
        <View style={styles.container}>
          <View style={styles.dashboard}>
            {/* Coming Soon Banner */}
            <View style={styles.comingSoonBanner}>
              <Feather name="info" size={14} color="#92400E" />
              <CustomText
                text="Analytics Coming Soon. The data shown below is a preview mockup."
                size="s"
                useTranslationText={false}
                style={styles.comingSoonText}
              />
            </View>
            {/* Back link */}
            <Pressable
              style={styles.backLink}
              onPress={() => navigation.goBack()}
              accessibilityRole="button"
              accessibilityLabel="Back to ILE Management">
              <Feather name="arrow-left" size={16} color="#475467" />
              <CustomText
                text="Back to ILE Management"
                size="s"
                useTranslationText={false}
                style={{ color: "#475467", marginLeft: 8 }}
              />
            </Pressable>

            {/* Main Title Card */}
            <View style={styles.card}>
              <View style={styles.titleRow}>
                <CustomText text={title} size="xl" weight="bold" useTranslationText={false} style={styles.titleText} />
                <View style={styles.activeBadge}>
                  <CustomText text="Active" size="xs" useTranslationText={false} style={styles.activeBadgeText} />
                </View>
              </View>

              <CustomText
                text="An interactive exploration of Roman civilization from its founding through the fall of the Western Empire, examining political, social, and cultural factors."
                size="s"
                useTranslationText={false}
                style={styles.description}
              />

              <View style={styles.metaRow}>
                <View style={styles.metaItem}>
                  <Feather name="calendar" size={12} color="#6B7280" />
                  <CustomText
                    text="Created Jan 15, 2026"
                    size="xs"
                    useTranslationText={false}
                    style={styles.metaText}
                  />
                </View>
                <View style={styles.metaItem}>
                  <Feather name="calendar" size={12} color="#6B7280" />
                  <CustomText text="Due Feb 28, 2026" size="xs" useTranslationText={false} style={styles.metaText} />
                </View>
                <CustomText
                  text="World History I, Political Science 101"
                  size="xs"
                  useTranslationText={false}
                  style={styles.metaText}
                />
              </View>

              <View style={styles.metricsRow}>
                <View style={styles.metaItem}>
                  <Feather name="clock" size={12} color="#6B7280" />
                  <CustomText text="18m" size="xs" useTranslationText={false} style={styles.metricText} />
                </View>
                <CustomText text="67% completion" size="xs" useTranslationText={false} style={styles.metricText} />
                <CustomText text="Avg score: 82%" size="xs" useTranslationText={false} style={styles.metricText} />
                <View style={styles.metaItem}>
                  <Feather name="check-circle" size={12} color="#6B7280" />
                  <CustomText
                    text="156 success points"
                    size="xs"
                    useTranslationText={false}
                    style={styles.metricText}
                  />
                </View>
                <View style={styles.metaItem}>
                  <Feather name="alert-circle" size={12} color="#6B7280" />
                  <CustomText
                    text="34 confusion points"
                    size="xs"
                    useTranslationText={false}
                    style={styles.metricText}
                  />
                </View>
              </View>

              <View style={styles.actionsRow}>
                <View style={styles.assignButton}>
                  <Feather name="user-plus" size={14} color="#FFFFFF" />
                  <CustomText
                    text="Assign"
                    size="s"
                    weight="medium"
                    useTranslationText={false}
                    style={{ color: "#FFFFFF", marginLeft: 6 }}
                  />
                </View>
                <View style={styles.iconButtons}>
                  <Feather name="eye" size={16} color="#9CA3AF" />
                  <Feather name="edit-2" size={16} color="#9CA3AF" />
                  <Feather name="link" size={16} color="#9CA3AF" />
                  <Feather name="clock" size={16} color="#9CA3AF" />
                  <Feather name="copy" size={16} color="#9CA3AF" />
                </View>
              </View>
            </View>

            {/* Tabs */}
            <View style={styles.tabsRow}>
              <View style={[styles.tabItem, styles.tabActive]}>
                <CustomText
                  text="Overview"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={styles.tabActiveText}
                />
              </View>
              <View style={styles.tabItem}>
                <CustomText text="Students" size="s" useTranslationText={false} style={styles.tabInactiveText} />
                <View style={styles.badgeNum}>
                  <CustomText text="6" size="xs" useTranslationText={false} style={styles.badgeNumText} />
                </View>
              </View>
            </View>

            {/* 4 KPI Cards */}
            <View style={styles.kpiGrid}>
              <View style={styles.kpiCard}>
                <View style={styles.kpiHeader}>
                  <Feather name="users" size={14} color="#6B7280" />
                  <CustomText text="Assigned" size="s" useTranslationText={false} style={styles.kpiLabel} />
                </View>
                <CustomText text="42" size="xxl" weight="bold" useTranslationText={false} style={styles.kpiValue} />
                <CustomText text="28 completed" size="xs" useTranslationText={false} style={styles.kpiSubValue} />
              </View>
              <View style={styles.kpiCard}>
                <View style={styles.kpiHeader}>
                  <Feather name="trending-up" size={14} color="#10B981" />
                  <CustomText text="Completion Rate" size="s" useTranslationText={false} style={styles.kpiLabel} />
                </View>
                <CustomText text="67%" size="xxl" weight="bold" useTranslationText={false} style={styles.kpiValue} />
              </View>
              <View style={styles.kpiCard}>
                <View style={styles.kpiHeader}>
                  <Feather name="clock" size={14} color="#3B82F6" />
                  <CustomText text="Avg Time Spent" size="s" useTranslationText={false} style={styles.kpiLabel} />
                </View>
                <CustomText text="18m" size="xxl" weight="bold" useTranslationText={false} style={styles.kpiValue} />
              </View>
              <View style={styles.kpiCard}>
                <View style={styles.kpiHeader}>
                  <Feather name="info" size={14} color="#EF4444" />
                  <CustomText text="Confusion Rate" size="s" useTranslationText={false} style={styles.kpiLabel} />
                </View>
                <CustomText text="18%" size="xxl" weight="bold" useTranslationText={false} style={styles.kpiValue} />
              </View>
            </View>

            {/* Sentiment Distribution */}
            <View style={styles.card}>
              <CustomText
                text="Sentiment Distribution"
                size="s"
                weight="bold"
                useTranslationText={false}
                style={styles.cardTitle}
              />
              <View style={styles.sentimentBarContainer}>
                <View
                  style={[
                    styles.sentimentSegment,
                    { flex: 70, backgroundColor: "#10B981", borderTopLeftRadius: 4, borderBottomLeftRadius: 4 },
                  ]}
                />
                <View style={[styles.sentimentSegment, { flex: 20, backgroundColor: "#F59E0B" }]} />
                <View
                  style={[
                    styles.sentimentSegment,
                    { flex: 10, backgroundColor: "#EF4444", borderTopRightRadius: 4, borderBottomRightRadius: 4 },
                  ]}
                />
              </View>
              <View style={styles.legendRow}>
                <View style={styles.legendItem}>
                  <View style={[styles.dot, { backgroundColor: "#10B981" }]} />
                  <CustomText text="Positive (20)" size="xs" useTranslationText={false} style={styles.legendText} />
                </View>
                <View style={styles.legendItem}>
                  <View style={[styles.dot, { backgroundColor: "#F59E0B" }]} />
                  <CustomText text="Neutral (6)" size="xs" useTranslationText={false} style={styles.legendText} />
                </View>
                <View style={styles.legendItem}>
                  <View style={[styles.dot, { backgroundColor: "#EF4444" }]} />
                  <CustomText text="Negative (2)" size="xs" useTranslationText={false} style={styles.legendText} />
                </View>
              </View>
            </View>

            {/* Recommendations Spotlight */}
            <View style={styles.recommendationCard}>
              <CustomText
                text="✨ Students are engaged with Roman political history and show strong connections to modern governance. Primary source analysis is a highlight, though timeline confusion around the Republic-to-Empire transition is a recurring gap."
                size="s"
                useTranslationText={false}
                style={styles.recText}
              />
              <CustomText
                text="Recommendations"
                size="xs"
                weight="bold"
                useTranslationText={false}
                style={styles.recTitle}
              />
              <View style={styles.bulletList}>
                <View style={styles.bulletItem}>
                  <View style={styles.bulletPoint} />
                  <CustomText
                    text="Add a timeline visualization activity before the Republic-to-Empire discussion"
                    size="xs"
                    useTranslationText={false}
                    style={styles.recText}
                  />
                </View>
                <View style={styles.bulletItem}>
                  <View style={styles.bulletPoint} />
                  <CustomText
                    text="Include a compare/contrast exercise for social class structures in Rome"
                    size="xs"
                    useTranslationText={false}
                    style={styles.recText}
                  />
                </View>
                <View style={styles.bulletItem}>
                  <View style={styles.bulletPoint} />
                  <CustomText
                    text="Provide additional scaffolding for economic analysis with guided source readings"
                    size="xs"
                    useTranslationText={false}
                    style={styles.recText}
                  />
                </View>
              </View>
            </View>

            {/* Time summary */}
            <View style={[styles.card, { flexDirection: "row", alignItems: "center", gap: 24, paddingVertical: 16 }]}>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <CustomText text="Time" size="xs" useTranslationText={false} style={styles.metaText} />
                <CustomText text="18m" size="s" weight="bold" useTranslationText={false} style={{ color: "#111827" }} />
              </View>
              <View style={{ width: 1, height: 16, backgroundColor: "#E5E7EB" }} />
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <CustomText text="Student Response" size="xs" useTranslationText={false} style={styles.metaText} />
                <CustomText text="38s" size="s" weight="bold" useTranslationText={false} style={{ color: "#111827" }} />
              </View>
              <View style={{ width: 1, height: 16, backgroundColor: "#E5E7EB" }} />
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <CustomText text="AI Response" size="xs" useTranslationText={false} style={styles.metaText} />
                <CustomText
                  text="1.2s"
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  style={{ color: "#111827" }}
                />
              </View>
            </View>

            {/* Class Originality */}
            <View style={styles.card}>
              <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 4 }}>
                <Feather name="award" size={14} color="#6B7280" />
                <CustomText
                  text="Class Originality"
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  style={{ marginLeft: 6, color: "#374151" }}
                />
              </View>
              <CustomText
                text="High — students are forming original connections between Roman governance and modern political systems. Primary source analysis shows independent thinking."
                size="s"
                useTranslationText={false}
                style={styles.description}
              />
            </View>

            {/* Learning Objectives */}
            <View style={styles.card}>
              <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 12 }}>
                <Feather name="target" size={14} color="#6B7280" />
                <CustomText
                  text="Learning Objectives (3)"
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  style={{ marginLeft: 6, color: "#374151" }}
                />
              </View>

              <View style={styles.objectiveItem}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                  <CustomText
                    text="1. Analyze the political structures of the Roman Republic and Empire"
                    size="s"
                    useTranslationText={false}
                    style={{ color: "#374151" }}
                  />
                  <Feather name="chevron-down" size={14} color="#9CA3AF" />
                </View>
                <View style={styles.metaRow}>
                  <View style={styles.metaItem}>
                    <Feather name="check" size={10} color="#6B7280" />
                    <CustomText text="4 success" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                  <View style={styles.metaItem}>
                    <Feather name="info" size={10} color="#6B7280" />
                    <CustomText text="1 confusion" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                  <View style={styles.metaItem}>
                    <Feather name="message-square" size={10} color="#6B7280" />
                    <CustomText text="1.8 qs" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                  <View style={styles.metaItem}>
                    <Feather name="clock" size={10} color="#6B7280" />
                    <CustomText text="6 min" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                </View>
              </View>

              <View style={styles.objectiveItem}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                  <CustomText
                    text="2. Evaluate the social and economic factors that contributed to Rome's decline"
                    size="s"
                    useTranslationText={false}
                    style={{ color: "#374151" }}
                  />
                  <Feather name="chevron-down" size={14} color="#9CA3AF" />
                </View>
                <View style={styles.metaRow}>
                  <View style={styles.metaItem}>
                    <Feather name="check" size={10} color="#6B7280" />
                    <CustomText text="3 success" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                  <View style={styles.metaItem}>
                    <Feather name="info" size={10} color="#6B7280" />
                    <CustomText text="2 confusion" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                  <View style={styles.metaItem}>
                    <Feather name="message-square" size={10} color="#6B7280" />
                    <CustomText text="2.1 qs" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                  <View style={styles.metaItem}>
                    <Feather name="clock" size={10} color="#6B7280" />
                    <CustomText text="7 min" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                </View>
              </View>

              <View style={styles.objectiveItemNoBorder}>
                <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 4 }}>
                  <CustomText
                    text="3. Compare Roman governance models with modern democratic systems"
                    size="s"
                    useTranslationText={false}
                    style={{ color: "#374151" }}
                  />
                  <Feather name="chevron-down" size={14} color="#9CA3AF" />
                </View>
                <View style={styles.metaRow}>
                  <View style={styles.metaItem}>
                    <Feather name="check" size={10} color="#6B7280" />
                    <CustomText text="3 success" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                  <View style={styles.metaItem}>
                    <Feather name="message-square" size={10} color="#6B7280" />
                    <CustomText text="1.4 qs" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                  <View style={styles.metaItem}>
                    <Feather name="clock" size={10} color="#6B7280" />
                    <CustomText text="5 min" size="xs" useTranslationText={false} style={styles.metaText} />
                  </View>
                </View>
              </View>
            </View>

            {/* What Went Well */}
            <View style={styles.successCard}>
              <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 8 }}>
                <Feather name="check-circle" size={14} color="#059669" />
                <CustomText
                  text="What Went Well"
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  style={{ marginLeft: 6, color: "#059669" }}
                />
              </View>
              <View style={styles.bulletList}>
                <View style={styles.bulletItem}>
                  <View style={[styles.bulletPoint, { backgroundColor: "#065F46" }]} />
                  <CustomText
                    text="Students demonstrated strong understanding of the Senate's role in Roman governance"
                    size="xs"
                    useTranslationText={false}
                    style={styles.description}
                  />
                </View>
                <View style={styles.bulletItem}>
                  <View style={[styles.bulletPoint, { backgroundColor: "#065F46" }]} />
                  <CustomText
                    text="Most students effectively connected Roman military expansion to economic growth"
                    size="xs"
                    useTranslationText={false}
                    style={styles.description}
                  />
                </View>
                <View style={styles.bulletItem}>
                  <View style={[styles.bulletPoint, { backgroundColor: "#065F46" }]} />
                  <CustomText
                    text="Primary source analysis skills showed significant improvement across the cohort"
                    size="xs"
                    useTranslationText={false}
                    style={styles.description}
                  />
                </View>
              </View>
            </View>

            {/* Areas of Confusion */}
            <View style={styles.alertCard}>
              <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 8 }}>
                <Feather name="alert-circle" size={14} color="#DC2626" />
                <CustomText
                  text="Areas of Confusion"
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  style={{ marginLeft: 6, color: "#DC2626" }}
                />
              </View>
              <View style={styles.bulletList}>
                <View style={styles.bulletItem}>
                  <View style={[styles.bulletPoint, { backgroundColor: "#991B1B" }]} />
                  <CustomText
                    text="Several students confused the timeline of the Republic-to-Empire transition"
                    size="xs"
                    useTranslationText={false}
                    style={styles.description}
                  />
                </View>
                <View style={styles.bulletItem}>
                  <View style={[styles.bulletPoint, { backgroundColor: "#991B1B" }]} />
                  <CustomText
                    text="The distinction between patricians and plebeians was unclear for some students"
                    size="xs"
                    useTranslationText={false}
                    style={styles.description}
                  />
                </View>
                <View style={styles.bulletItem}>
                  <View style={[styles.bulletPoint, { backgroundColor: "#991B1B" }]} />
                  <CustomText
                    text="Economic factors of Rome's decline were oversimplified by lower-performing students"
                    size="xs"
                    useTranslationText={false}
                    style={styles.description}
                  />
                </View>
              </View>
            </View>
            <CustomSpacing type="vertical" size="xxl" />
          </View>
        </View>
      )}
    </LTILayout>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 48,
  },
  loadingText: {
    color: "#6B7280",
  },
  comingSoonBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#FFFBEB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FDE68A",
    padding: 12,
  },
  comingSoonText: {
    color: "#92400E",
    flex: 1,
  },
  container: {
    backgroundColor: "#F9FAFB",
    padding: 24,
    width: "100%",
  },
  dashboard: {
    maxWidth: 1000,
    width: "100%",
    alignSelf: "center",
    gap: 16,
  },
  backLink: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.03,
    shadowRadius: 2,
    elevation: 1,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  titleText: {
    color: "#111827",
    marginRight: 12,
  },
  activeBadge: {
    borderColor: "#10B981",
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  activeBadgeText: {
    color: "#059669",
  },
  description: {
    color: "#4B5563",
    lineHeight: 20,
    marginBottom: 12,
  },
  metaRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 12,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  metaText: {
    color: "#6B7280",
  },
  metricsRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 16,
    marginBottom: 24,
  },
  metricText: {
    color: "#374151",
    fontWeight: "500",
  },
  actionsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  assignButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#3B82F6",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  iconButtons: {
    flexDirection: "row",
    gap: 16,
  },
  tabsRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 8,
  },
  tabItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  tabActive: {
    backgroundColor: "#EFF6FF",
  },
  tabActiveText: {
    color: "#2563EB",
  },
  tabInactiveText: {
    color: "#6B7280",
  },
  badgeNum: {
    backgroundColor: "#F3F4F6",
    borderRadius: 10,
    paddingHorizontal: 6,
    marginLeft: 6,
  },
  badgeNumText: {
    color: "#4B5563",
  },
  kpiGrid: {
    flexDirection: "row",
    gap: 16,
    flexWrap: "wrap",
  },
  kpiCard: {
    flex: 1,
    minWidth: 150,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 16,
  },
  kpiHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 12,
  },
  kpiLabel: {
    color: "#4B5563",
  },
  kpiValue: {
    color: "#111827",
  },
  kpiSubValue: {
    color: "#6B7280",
    marginTop: 4,
  },
  cardTitle: {
    color: "#111827",
    marginBottom: 12,
  },
  sentimentBarContainer: {
    height: 12,
    flexDirection: "row",
    marginBottom: 12,
  },
  sentimentSegment: {
    height: "100%",
  },
  legendRow: {
    flexDirection: "row",
    gap: 16,
  },
  legendItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  legendText: {
    color: "#6B7280",
  },
  recommendationCard: {
    backgroundColor: "#EFF6FF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#DBEAFE",
    padding: 20,
  },
  recTitle: {
    color: "#4338CA",
    marginTop: 12,
    marginBottom: 8,
  },
  recText: {
    color: "#312E81",
    lineHeight: 20,
  },
  bulletList: {
    marginLeft: 4,
    gap: 6,
  },
  bulletItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  bulletPoint: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: "#4338CA",
    marginTop: 8,
  },
  objectiveItem: {
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
  },
  objectiveItemNoBorder: {
    paddingVertical: 12,
  },
  successCard: {
    backgroundColor: "#F0FDF4",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#BBF7D0",
    padding: 20,
  },
  alertCard: {
    backgroundColor: "#FEF2F2",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#FECACA",
    padding: 20,
  },
});

export default LTIAnalyticsView;
